import {
  Modal,
  Text,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useToast,
  Button,
  Stack,
} from '@chakra-ui/react';
import { SuperFundResponse } from '@zap-onboard/api-client';
import { BusinessNominatedSuperInfoResponse } from '@zap-onboard/api-client/src/business/BusinessClient';
import React, { useEffect, useState } from 'react';
import { useBillingMutations } from '../../../billing/hooks/useBillingMutations';
import { Spinner } from '../../../components/Spinner/Spinner';
import { useHandler } from '../../../hooks';
import { useAPI } from '../../../services/API';
import { Embed } from '../../../superapi';
import { MESSAGE_KIND } from '../../../superapi/services/message';
import { NominateSuperFundForm } from './NominateSuperFundForm';

export const NominateSuperFundModal = (props: {
  isOpen: boolean;
  onClose: () => unknown;
  info: BusinessNominatedSuperInfoResponse;
  context?: 'assignmentBlocker';
}) => {
  const { isOpen, onClose, info } = props;

  const [step, setStep] = useState<'select' | 'warn'>(
    props.context === 'assignmentBlocker' ? 'warn' : 'select',
  );

  const [doUpdateSuper] = useHandler(
    (api) => api.business().updateBusinessNominatedSuper,
    {
      invalidates: () => [['BUSINESS_NOMINATED_SUPER_FUND'], ['BUSINESS']],
    },
  );

  if (step === 'warn') {
    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent m={4}>
          <ModalHeader>Configure super</ModalHeader>
          <ModalCloseButton />

          <ModalBody fontSize="sm" pb={6}>
            <Stack spacing={4} p={4}>
              <Text>
                Before you can collect superannuation information you need to
                select a default fund
              </Text>
              <Button onClick={() => setStep('select')}>Nominate a fund</Button>
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={info.configuration?.provider === 'SUPER_API' ? '4xl' : undefined}
    >
      <ModalOverlay />
      <ModalContent m={4}>
        {info.configuration?.provider === 'MANUAL' && (
          <ModalHeader fontSize="md">Nominate a Super Fund</ModalHeader>
        )}
        <ModalCloseButton />

        <ModalBody fontSize="sm" pb={6}>
          {info.configuration?.provider === 'MANUAL' && (
            <NominateSuperFundForm after={onClose} fund={info.fund} />
          )}
          {info.configuration?.provider === 'SUPER_API' &&
            !!info.configuration.superAPIConfigurationEmbedURL && (
              <SuperAPIEmployerEmbed
                key={info.configuration.superAPIConfigurationEmbedURL}
                onComplete={() => {
                  doUpdateSuper({ provider: 'SUPER_API' })?.tap(onClose) ||
                    onClose();
                }}
                embedURL={info.configuration.superAPIConfigurationEmbedURL}
              />
            )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const SuperAPIEmployerEmbed = (props: {
  embedURL: string;
  onComplete: () => unknown;
}) => {
  const embedRef = React.useRef<null | HTMLDivElement>(null);

  /** This is needed to prevent closure over the onComplete handler when this
   * component loads. This is a very hard bug to reproduce but becomes obvious
   * when you click through fast enough to not trigger re-renders */
  const onCompleteRef = React.useRef(props.onComplete);
  onCompleteRef.current = props.onComplete;

  const [loadingiFrame, setLoadingiFrame] = useState<boolean>(true);

  const toast = useToast();

  React.useEffect(() => {
    if (embedRef.current === null) {
      return;
    }

    console.log(`SuperAPI: Embed initializing to ${props.embedURL}`);
    const embedInstance = new Embed({
      element: embedRef.current,
      url: props.embedURL,
    });

    embedInstance.on(MESSAGE_KIND.TOAST, (r) => {
      const data = r as any;

      console.log(`SuperAPI: Received toast ${JSON.stringify(r)}`);
      if (typeof data?.message !== 'string') {
        console.error('Embed message did not return message for toast', data);
        return;
      }

      if (data.kind === 'info') {
        toast({
          status: 'info',
          title: data.message,
        });
      }

      if (data.kind === 'success') {
        toast({
          status: 'success',
          title: data.message,
        });
      }
      if (data.kind === 'error') {
        toast({
          status: 'error',
          title: data.message,
        });
      }

      if (data.kind === 'warning') {
        toast({
          status: 'warning',
          title: data.message,
        });
      }
    });

    embedInstance.on(MESSAGE_KIND.EMPLOYER_SETTINGS_COMMITTED, () => {
      console.log(`SuperAPI: Received committed`);
      onCompleteRef.current?.();
    });
    embedInstance.on(MESSAGE_KIND.EMPLOYER_SETTINGS_UPDATED, () => {
      console.log('SuperAPI: Received updated');
      onCompleteRef.current?.();
    });
    embedInstance.on(MESSAGE_KIND.LOADED, () => {
      console.log('SuperAPI: iFrame loaded');
      setLoadingiFrame(false);
    });

    return () => {
      if (embedInstance !== null) {
        console.log('SuperAPI: Tearing down embed');
        try {
          embedInstance.teardown();
        } catch (e) {
          console.error('Error tearing down embed', e);
        }
      } else {
        console.log('SuperAPI: Not tearing down embed');
      }
    };
  }, []);

  return <div ref={embedRef}>{loadingiFrame && <Spinner />}</div>;
};
